import { Grid } from "@mui/material";
import Badge from "shared/Badge/Badge";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { CURRENCIES, VENUE_PRICE_TIMES_MODULES } from "constants/venueConstants";
import { Currency } from "types/currency";
import { VenuePriceTimeModule } from "types/time";
import { useSelector } from "react-redux";
import { RootState } from "store";
import { appConstants } from "constants/appConstants";

export default function HomePriceOverview({ priceList }: any) {
  const { language } = useSelector((state: RootState) => state.lang);

  const { t } = useTranslation();

  const getCurrency = (currencyId: number) => {
    return CURRENCIES.find((el: Currency) => el.index === currencyId)?.label;
  };

  const getTimeUnit = (timeIndex: number) => {
    return t(`${VENUE_PRICE_TIMES_MODULES.find((el: VenuePriceTimeModule) => el.index === timeIndex)?.name}`);
  };

  const getWeekdays = (weekdays: string) => {
    const weekdaysArr = weekdays.split(",");

    const words = language === appConstants.LANGUAGE.DEFAULT_LANGUAGE ? ["零", "一", "二", "三", "四", "五", "六", "日"] : ["", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    if (weekdaysArr.length === 7) return t("everyday");

    return ` ${language === appConstants.LANGUAGE.DEFAULT_LANGUAGE ? "週" : ""}${weekdaysArr
      .sort()
      .map((item: string) => words[Number(item) as number])
      .join("、")}`;
  };

  const getWeekdaysTime = (startTime: string, endTime: string) => {
    return moment(startTime, "HH:mm:ss").format("HH:mm") + " - " + moment(endTime, "HH:mm:ss").format("HH:mm");
  };

  const getOneTimePeriodTime = (startTime: string) => {
    return moment(startTime).format("Do MMM YY HH:mm");
  };

  return (
    <div className="listingSection__wrap">
      {/* HEADING */}
      <div>
        <h2 className="text-2xl font-semibold">{t("priceAndOpening")}</h2>
        <span className="block mt-2 text-neutral-500 dark:text-neutral-400">{t("priceAndOpening_desc")}</span>
      </div>
      <div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>
      {/* CONTENT */}

      <div className="text-sm sm:text-base text-neutral-6000 dark:text-neutral-300 -mb-4">
        {priceList?.default && (
          <>
            <h4 className="text-lg font-semibold">{t("price_basic_charge")}</h4>
            <br />
            <div className="flow-root">
              <div className="p-4 flex justify-between items-center space-x-4 rounded-lg">
                {language === appConstants.LANGUAGE.DEFAULT_LANGUAGE ? priceList?.default?.price_name_tc : priceList?.default?.price_name_en}

                <span>
                  {getCurrency(priceList?.default.currency_id)} {priceList?.default.price} {t("per")}
                  {priceList?.default.time == "1" ? "" : ` ${priceList?.default.time} `}
                  {getTimeUnit(priceList?.default.time_unit_type)}
                </span>
              </div>
            </div>
          </>
        )}

        <br />
        {priceList?.repeat?.length > 0 && (
          <div className="text-sm sm:text-base text-neutral-6000 dark:text-neutral-300 -mb-4">
            <h4 className="text-lg font-semibold">{t("price_weekly_charges")}</h4>
            <br />
            {priceList?.repeat?.map((item: any, index: number) => (
              <div
                key={index}
                className={
                  !(index % 2) ? "p-4 bg-neutral-100 dark:bg-neutral-800 flex justify-between items-center space-x-4 rounded-lg" : "p-4 flex justify-between items-center space-x-4 rounded-lg"
                }>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    {language === appConstants.LANGUAGE.DEFAULT_LANGUAGE ? item?.price_name_tc : item?.price_name_en}
                  </Grid>
                  <Grid item xs={12}>
                    <Badge color="green" name={getWeekdays(item.weekdays)} />
                  </Grid>
                  <Grid item xs={12}>
                    <Badge color="yellow" name={getWeekdaysTime(item.start_time, item.end_time)} />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item md={12} xs={12} textAlign={"end"}>
                    {getCurrency(item.currency_id)} {item.price} {t("per")}
                    {item.time == "1" ? "" : ` ${item.time} `}
                    {getTimeUnit(item.time_unit_type)}
                  </Grid>
                </Grid>
              </div>
            ))}
          </div>
        )}
        <br />
        <br />
        {priceList?.otp?.length > 0 && (
          <div className="text-sm sm:text-base text-neutral-6000 dark:text-neutral-300 -mb-4">
            <h4 className="text-lg font-semibold">{t("price_special_period")} *</h4>
            <br />
            {priceList?.otp?.map((item: any, index: number) => (
              <div
                key={index}
                className={
                  !(index % 2) ? "p-4 bg-neutral-100 dark:bg-neutral-800 flex justify-between items-center  space-x-4 rounded-lg" : "p-4 flex justify-between items-center space-x-4 rounded-lg"
                }>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    {language === appConstants.LANGUAGE.DEFAULT_LANGUAGE ? item?.price_name_tc : item?.price_name_en}
                  </Grid>
                  <Grid item xs={12} textAlign="left">
                    <span style={{ marginRight: 5 }}>{t("from")}</span>
                    <Badge color="blue" name={getOneTimePeriodTime(item.start_date_time)} />
                  </Grid>
                  <Grid item xs={12} textAlign="left">
                    <span style={{ marginRight: 5 }}>{t("to")}</span>
                    <Badge color="red" name={getOneTimePeriodTime(item.end_date_time)} />
                  </Grid>
                </Grid>
                <Grid container spacing={2}>
                  <Grid item md={12} xs={12} textAlign={"end"}>
                    {getCurrency(item.currency_id)} {item.price} {t("per")}
                    {item.time == "1" ? "" : ` ${item.time} `}
                    {getTimeUnit(item.time_unit_type)}
                  </Grid>
                </Grid>
              </div>
            ))}
          </div>
        )}
        <br />
        <br />
      </div>
    </div>
  );
}
